import { NgClass } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnInit,
  inject,
} from '@angular/core'
import {
  AbstractControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms'
import { DateAdapter, MatNativeDateModule } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { DateFilter } from '@wm-front/admin/graphql'
import dayJS, { Dayjs } from 'dayjs'
import { filter, tap } from 'rxjs'
import { CustomNestedForm } from '../../helpers/custom-nested-form'
import { FormError } from '../../interfaces/form-error.interface'
import { FormErrorComponent } from '../form-error/form-error.component'
import { MonthRangePickerComponent } from '../month-range-picker/month-range-picker.component'
import { MatIconModule } from '@angular/material/icon'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { I18nService } from '@wm-front/core'

@UntilDestroy()
@Component({
  selector: 'wm-front-input-datepicker',
  templateUrl: './input-datepicker.component.html',
  styleUrls: ['./input-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => InputDatepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => InputDatepickerComponent),
      multi: true,
    },
  ],
  imports: [
    NgClass,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    FormsModule,
    MonthRangePickerComponent,
    FormErrorComponent,
    MatFormFieldModule,
    MatInputModule,
  ],
})
export class InputDatepickerComponent
  extends CustomNestedForm
  implements OnInit
{
  /** The input's label */
  @Input() public label?: string

  /** Draws the required mark after the label (PRENG-8287), instead of a star written in the label's translation */
  @Input() public requiredMark = false

  /** The form group defined in the  parent component */
  @Input() public formGroup: UntypedFormGroup

  /** The control name defined in the  parent component */
  @Input() public controlName: string

  /** Earliest pickable dat for datepicker */
  @Input() public min: string

  /** Last pickable date for datepicker */
  @Input() public max: string

  /** Minimum range for range pickers */
  @Input() public minRange: number

  /** The input's placeholder */
  @Input() public placeholder: string = 'JJ/MM/AAAA'

  /** The error for the current form control */
  @Input() public error: FormError

  /** Whether the form control should be disabled */
  @Input() public disabled: boolean

  /** Whether it is a month range picker */
  @Input() public isMonthRangePicker: boolean

  /** Whether to show the month range picker */
  @Input() public showMonthDatePicker: boolean

  /** The month range used by picker */
  public monthRangeValue: DateFilter = { after: null, before: null }

  private _materialDateAdapter = inject<DateAdapter<Dayjs>>(DateAdapter)

  private _i18Service = inject(I18nService)

  /** The label for a month by month range
   * @returns { string }
   */
  public get rangeLabel(): string {
    const period = this.formControl?.value as DateFilter

    if (period?.after && period?.before) {
      return `${dayJS(period.after).format('MMMM YYYY')} - ${dayJS(
        period.before
      ).format('MMMM YYYY')}`
    } else {
      return ''
    }
  }

  /** The form control reference
   * @returns { AbstractControl | null }
   */
  public get formControl(): AbstractControl | null {
    return this.form && this.form.get(this.controlName)
  }

  /** There is an error
   * @returns { boolean }
   */
  public get hasError(): boolean {
    return !!(
      this.formControl &&
      !this.formControl.valid &&
      !this.formControl.pristine &&
      this.formControl.touched
    )
  }

  public ngOnInit(): void {
    this.setForm()
    dayJS.locale(this._i18Service.languageWithoutLocale)
    this._materialDateAdapter.setLocale(this._i18Service.languageWithoutLocale)
    this.manageFormControlValueChange()
  }

  /** Update value in form
   * @param { Date }
   */
  public updateValue(val: Date | DateFilter): void {
    if (
      this.isMonthRangePicker &&
      (val as DateFilter).before &&
      (val as DateFilter).after
    ) {
      this.monthRangeValue = val as DateFilter
      this.showMonthDatePicker = false
    }

    this.formControl?.setValue(val)
    this.formControl?.markAsDirty()
    this._onChangedCallback()
  }

  /** Resets the value of the input */
  public reset(): void {
    if (this.isMonthRangePicker) {
      this.monthRangeValue = { after: null, before: null }
      this.formControl?.setValue({ after: null, before: null })
    } else {
      this.formControl?.setValue('')
    }
  }

  /** Resets the input field on escape */
  public onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      this.reset()
    }
  }

  /**  Set touched on blur **/
  public override onBlur(): void {
    this._onTouchedCallback()
    this.formControl?.markAsTouched()
  }

  /**
   * Will set the form
   * @private
   */
  private setForm(): void {
    if (this.formGroup) {
      this.form = this.formGroup
    }
  }

  /**
   * Will manage some cases when the value will be set by outside components
   * @private
   */
  private manageFormControlValueChange(): void {
    this.formControl?.valueChanges
      .pipe(
        filter(
          (range: DateFilter) =>
            this.isMonthRangePicker && !range.before && !range.after
        ),
        tap((range) => (this.monthRangeValue = range)),
        untilDestroyed(this)
      )
      .subscribe()
  }
}
